import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AuthorizedRoute, { RedirectToRoleHomePage } from 'utils/AuthorizedRoute';
import { HomeLinkRoutes } from 'constants/routes';
import './App.css';
import './scss/styles.scss';
import { useAuth } from 'utils/AuthProvider';
import { Role } from 'model/account';
import UnauthorizedRoute from 'utils/UnauthorizedRoute';
import { AppointmentType } from 'hooks/useAppointments';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { Loader } from 'components/Loader';
import { MedicalViewContainer } from 'views/Medical/MedicalViewContainer';

// Lazy load components for code splitting
const Login = lazy(() => import('views/PreLogin/Login'));
const Register = lazy(() => import('views/PreLogin/Register'));
const ResetPassword = lazy(() => import('views/PreLogin/ResetPassword'));
const RequestView = lazy(() => import('views/Medical/RequestView'));
const Logout = lazy(() =>
  import('views/PreLogin/Logout').then((module) => ({
    default: module.Logout,
  })),
);
const TermsAndConditions = lazy(() =>
  import('views/PreLogin/Terms').then((module) => ({
    default: module.TermsAndConditions,
  })),
);
const ExchangeToken = lazy(() =>
  import('views/PreLogin/ExchangeToken').then((module) => ({
    default: module.ExchangeToken,
  })),
);

// Heavy components that should be split
const MedicalHome = lazy(() => import('views/Medical/MedicalHome'));
const EditAppointmentPage = lazy(
  () => import('views/Medical/Appointment/EditAppointmentPage'),
);
const RebookAppointmentPage = lazy(
  () => import('views/Medical/Appointment/RebookAppointmentPage'),
);
const ScheduleHome = lazy(() => import('views/Medical/ScheduleView'));
const PaymentHistoryView = lazy(
  () => import('views/Medical/PaymentHistoryView'),
);
const VideoCall = lazy(() =>
  import('~/views/common/videocall').then((module) => ({
    default: module.VideoCall,
  })),
);

// Patient views
const ViewContainer = lazy(() =>
  import('views/Patient/ViewContainer').then((module) => ({
    default: module.ViewContainer,
  })),
);
const Pharmacy = lazy(() =>
  import('views/Patient/pharmacy').then((module) => ({
    default: module.Pharmacy,
  })),
);
const Medical = lazy(() =>
  import('views/Patient/medical').then((module) => ({
    default: module.Medical,
  })),
);
const Notifications = lazy(() =>
  import('views/Patient/notifications').then((module) => ({
    default: module.Notifications,
  })),
);
const Chat = lazy(() =>
  import('views/Patient/chat').then((module) => ({ default: module.Chat })),
);
const MedicalChat = lazy(() =>
  import('views/Medical/chat').then((module) => ({
    default: module.MedicalChat,
  })),
);
const Appointments = lazy(() =>
  import('views/Patient/appointments').then((module) => ({
    default: module.Appointments,
  })),
);
const CreateNewAppointment = lazy(() =>
  import('views/Patient/createAppointment').then((module) => ({
    default: module.CreateNewAppointment,
  })),
);
const PatientProfile = lazy(() =>
  import('views/Patient/profile').then((module) => ({
    default: module.PatientProfile,
  })),
);
const ProfileInfo = lazy(() =>
  import('views/Patient/profile/ProfileInfo').then((module) => ({
    default: module.ProfileInfo,
  })),
);
const ProfileInfoMobile = lazy(() =>
  import('views/Patient/profile/ProfileInfoMobile').then((module) => ({
    default: module.ProfileInfoMobile,
  })),
);
const VerifyPayment = lazy(() =>
  import('views/Patient/verifyPayment').then((module) => ({
    default: module.VerifyPayment,
  })),
);

function App() {
  const { token, role } = useAuth();
  const { isMobile } = useWindowDimensions();

  const HandleOtherRoutes = () => {
    if (token) {
      return <Navigate to={HomeLinkRoutes.redirect} />;
    }
    return <Navigate to={HomeLinkRoutes.login} />;
  };

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route element={<UnauthorizedRoute />}>
          <Route path={HomeLinkRoutes.login} element={<Login />} />
        </Route>
        <Route element={<UnauthorizedRoute />}>
          <Route path={HomeLinkRoutes.register} element={<Register />} />
        </Route>
        <Route element={<UnauthorizedRoute />}>
          <Route
            path={HomeLinkRoutes.resetPassword}
            element={<ResetPassword />}
          />
        </Route>
        <Route path={HomeLinkRoutes.logout} element={<Logout />} />

        <Route
          element={<AuthorizedRoute pagePermissionRole={[Role.PATIENT]} />}
        >
          <Route path={HomeLinkRoutes.patient} element={<ViewContainer />}>
            <Route path={HomeLinkRoutes.patientMedical} element={<Medical />} />
            <Route
              path={HomeLinkRoutes.patientPharmacy}
              element={<Pharmacy />}
            />
            <Route
              path={HomeLinkRoutes.patientMedicalVerifyPayment}
              element={<VerifyPayment type={AppointmentType.medicalcare} />}
            />
            <Route
              path={HomeLinkRoutes.patientPharmacyVerifyPayment}
              element={
                <VerifyPayment type={AppointmentType.medicine_delivery} />
              }
            />
            <Route
              path={HomeLinkRoutes.patientAppointments}
              element={<Appointments />}
            />
            <Route path={HomeLinkRoutes.patientChat} element={<Chat />} />
            <Route path={HomeLinkRoutes.patientHelp} element={<Pharmacy />} />
            <Route
              path={HomeLinkRoutes.patientCreateNewPharmacyAppointment}
              element={
                <CreateNewAppointment
                  type={AppointmentType.medicine_delivery}
                />
              }
            />
            <Route
              path={HomeLinkRoutes.patientCreateNewMedicalAppointment}
              element={
                <CreateNewAppointment type={AppointmentType.medicalcare} />
              }
            />
            <Route
              path={HomeLinkRoutes.patientVideoCall}
              element={<VideoCall isMedical={false} />}
            />
            <Route
              path={HomeLinkRoutes.patientProfile}
              element={<PatientProfile />}
            />
            <Route
              path={HomeLinkRoutes.patientProfileInfo}
              element={isMobile ? <ProfileInfoMobile /> : <ProfileInfo />}
            />
            <Route
              path={HomeLinkRoutes.patientProfileInfoInitial}
              element={
                isMobile ? (
                  <ProfileInfoMobile isInitialSetup />
                ) : (
                  <ProfileInfo isInitialSetup />
                )
              }
            />
            <Route
              path={HomeLinkRoutes.patientNotifications}
              element={<Notifications />}
            />
            <Route
              path="*"
              element={<Navigate to={HomeLinkRoutes.patientMedical} replace />}
            />
          </Route>
        </Route>
        <Route
          element={
            <AuthorizedRoute
              pagePermissionRole={[Role.DOCTOR, Role.PHARMACIST]}
            />
          }
        >
          <Route
            path={HomeLinkRoutes.medical}
            element={<MedicalViewContainer />}
          >
            <Route
              path={HomeLinkRoutes.medicalAppointmentEdit}
              element={<EditAppointmentPage />}
            />
            <Route
              path={HomeLinkRoutes.medicalAppointmentRebook}
              element={<RebookAppointmentPage />}
            />
            <Route
              path={HomeLinkRoutes.medicalAppointments}
              element={<MedicalHome />}
            />
            <Route
              path={HomeLinkRoutes.medicalSchedule}
              element={<ScheduleHome />}
            />
            <Route
              path={HomeLinkRoutes.medicalPaymentHistory}
              element={<PaymentHistoryView />}
            />
            <Route
              path={HomeLinkRoutes.medicalChat}
              element={<MedicalChat />}
            />
            <Route
              path={HomeLinkRoutes.medicalVideoCall}
              element={<VideoCall isMedical={true} />}
            />
            <Route
              path={HomeLinkRoutes.medicalAppointmentRequests}
              element={<RequestView />}
            />
            <Route
              path="*"
              element={
                <Navigate to={HomeLinkRoutes.medicalAppointments} replace />
              }
            />
          </Route>
        </Route>
        <Route
          path={HomeLinkRoutes.redirect}
          element={<RedirectToRoleHomePage role={role} />}
        />
        <Route path={HomeLinkRoutes.terms} element={<TermsAndConditions />} />
        <Route
          path={HomeLinkRoutes.exchangeToken}
          element={<ExchangeToken />}
        />
        <Route path="*" element={<HandleOtherRoutes />} />
      </Routes>
    </Suspense>
  );
}

export default App;
